import type { TranslationKey } from '../../i18n';
import type { StoreCollectionKey } from '../../types/product';
import type {
  StorefrontAnnouncementBarChromeConfig,
  StorefrontAnnouncementBarLinkTarget,
  StorefrontAnySectionConfig,
  StorefrontFooterChromeConfig,
  StorefrontFooterQuickLinkTarget,
  StorefrontHeaderChromeConfig,
  StorefrontHeaderNavigationTarget,
  StorefrontHeroLocalizedText,
  StorefrontMobileNavChromeConfig,
  StorefrontMobileNavItem,
  StorefrontMobileNavigationTarget,
  StorefrontNavigationContactMethod,
  StorefrontNavigationItem,
  StorefrontNavigationTarget,
  StorefrontPageConfig,
  StorefrontPageConfigV2,
  StorefrontSafeSocialUrl,
  StorefrontSectionConfigV2,
  StorefrontSectionContentConfig,
  StorefrontSectionPlacement,
  StorefrontSectionType,
  StorefrontSocialLink,
  StorefrontSocialProvider,
  StorefrontValidationErrorCode,
} from '../../types/storefront-engine';
import type { PublicStore } from '../../types/store';
import { toStoreCollectionRouteKey } from '../store-home-merchandising';
import { resolveStorefrontSeoText, sanitizeStorefrontSeoLocalizedText } from './seo-metadata';

type UnknownRecord = Record<string, unknown>;

export const STOREFRONT_HEADER_PRIMARY_LINK_LIMIT = 6;
export const STOREFRONT_FOOTER_QUICK_LINK_LIMIT = 8;
export const STOREFRONT_FOOTER_SOCIAL_LINK_LIMIT = 4;
export const STOREFRONT_MOBILE_NAV_ITEM_MIN = 2;
export const STOREFRONT_MOBILE_NAV_ITEM_MAX = 5;
export const STOREFRONT_SOCIAL_URL_MAX_LENGTH = 2048;
/** F-142. Per-locale announcement text limit (after whitespace normalization); mirrored in SQL. */
export const STOREFRONT_ANNOUNCEMENT_BAR_TEXT_MAX_LENGTH = 140;
/** F-142. Canonical instance id of the single announcement-bar chrome section. */
export const STOREFRONT_ANNOUNCEMENT_BAR_SECTION_ID = 'home-announcement-bar';

export const STOREFRONT_HEADER_BRAND_DISPLAY_OPTIONS = [
  'logo',
  'name',
  'logo-and-name',
] as const satisfies readonly StorefrontHeaderChromeConfig['brandDisplay'][];

export const STOREFRONT_FOOTER_BRAND_DISPLAY_OPTIONS = [
  'dukanti-and-store',
  'store-only',
  'dukanti-only',
] as const satisfies readonly StorefrontFooterChromeConfig['brandDisplay'][];

export const STOREFRONT_NAVIGATION_CONTACT_METHODS = [
  'whatsapp',
  'phone',
] as const satisfies readonly StorefrontNavigationContactMethod[];

export const STOREFRONT_SOCIAL_PROVIDERS = [
  'instagram',
  'facebook',
] as const satisfies readonly StorefrontSocialProvider[];

export const STOREFRONT_COLLECTION_NAVIGATION_KEYS = [
  'best_sellers',
  'trending_now',
  'new_arrivals',
] as const satisfies readonly StoreCollectionKey[];

/**
 * Panel/settings view of the announcement bar: status + content in one object, because the
 * merchant edits both in one place (F-142).
 */
export interface StorefrontAnnouncementBarSettings extends StorefrontAnnouncementBarChromeConfig {
  readonly enabled: boolean;
}

export type StorefrontGlobalChromeSettings = {
  /**
   * F-142. OPTIONAL for backward compatibility: callers written before the announcement bar
   * existed pass the three-surface shape `{ header, footer, mobileNav }`, and
   * `withStorefrontGlobalChromeSettings` then leaves the bar section UNTOUCHED (it never resets
   * it to the platform default — a caller that does not know the bar must not be able to wipe a
   * merchant's bar text). `resolveStorefrontGlobalChromeSettings` always returns it populated
   * (see `StorefrontResolvedGlobalChromeSettings`).
   */
  readonly announcementBar?: StorefrontAnnouncementBarSettings;
  readonly header: StorefrontHeaderChromeConfig;
  readonly footer: StorefrontFooterChromeConfig;
  readonly mobileNav: StorefrontMobileNavChromeConfig;
};

/** Chrome settings as read back from a config: every surface, including the bar, is present. */
export type StorefrontResolvedGlobalChromeSettings = StorefrontGlobalChromeSettings & {
  readonly announcementBar: StorefrontAnnouncementBarSettings;
};

export type StorefrontGlobalChromeEditableSurface =
  | 'announcement-bar'
  | 'header'
  | 'footer'
  | 'mobile-nav';

/** Every `global.*` chrome section type (F-142 widened this to four). */
export type StorefrontGlobalChromeSectionType = Extract<StorefrontSectionType, `global.${string}`>;

interface StorefrontGlobalChromeSectionDefinition {
  readonly id: string;
  readonly placement: StorefrontSectionPlacement;
  readonly surface: StorefrontGlobalChromeEditableSurface;
  readonly contentKey: keyof StorefrontSectionContentConfig;
}

/**
 * Single source of truth for chrome identity (F-142). Every other chrome map derives from or is
 * pinned against this record: canonical section id, canonical placement, the editable surface
 * name and the `config.content` key that holds the surface's chrome config. Entries are in
 * render order (bar first).
 */
export const GLOBAL_CHROME_SECTION_BY_TYPE = {
  'global.announcementBar': {
    id: 'home-announcement-bar',
    placement: 'global-announcement-bar',
    surface: 'announcement-bar',
    contentKey: 'announcementBar',
  },
  'global.header': {
    id: 'home-header',
    placement: 'global-header',
    surface: 'header',
    contentKey: 'header',
  },
  'global.footer': {
    id: 'home-footer',
    placement: 'global-footer',
    surface: 'footer',
    contentKey: 'footer',
  },
  'global.mobileNav': {
    id: 'home-mobile-nav',
    placement: 'mobile-nav',
    surface: 'mobile-nav',
    contentKey: 'mobileNav',
  },
} as const satisfies Record<
  StorefrontGlobalChromeSectionType,
  StorefrontGlobalChromeSectionDefinition
>;

export function isStorefrontGlobalChromeSectionType(
  value: string,
): value is StorefrontGlobalChromeSectionType {
  return Object.prototype.hasOwnProperty.call(GLOBAL_CHROME_SECTION_BY_TYPE, value);
}

interface StorefrontGlobalChromeSurfaceDefinition {
  readonly surface: StorefrontGlobalChromeEditableSurface;
  readonly id: string;
  readonly type: StorefrontGlobalChromeSectionType;
  readonly placement: StorefrontSectionPlacement;
}

const GLOBAL_CHROME_EDITABLE_SURFACES: readonly StorefrontGlobalChromeSurfaceDefinition[] = (
  Object.keys(GLOBAL_CHROME_SECTION_BY_TYPE) as StorefrontGlobalChromeSectionType[]
).map((type) => {
  const definition = GLOBAL_CHROME_SECTION_BY_TYPE[type];
  return { surface: definition.surface, id: definition.id, type, placement: definition.placement };
});

export type StorefrontNavigationView = {
  readonly id: string;
  readonly target:
    | StorefrontNavigationTarget
    | { readonly type: 'social'; readonly provider: StorefrontSocialProvider };
  readonly href: string;
  readonly labelKey: TranslationKey;
  readonly isExternal: boolean;
};

export type StorefrontNavigationRouteBase = `/s/${string}`;

export interface StorefrontNavigationHrefOptions {
  readonly routeBase?: StorefrontNavigationRouteBase;
}

export const DEFAULT_STOREFRONT_HEADER_CHROME_CONFIG: StorefrontHeaderChromeConfig = {
  brandDisplay: 'logo-and-name',
  primaryLinks: [
    { id: 'home', target: { type: 'store-home' } },
    { id: 'categories', target: { type: 'store-categories' } },
    { id: 'sales', target: { type: 'store-sales' } },
  ],
};

export const DEFAULT_STOREFRONT_FOOTER_CHROME_CONFIG: StorefrontFooterChromeConfig = {
  brandDisplay: 'dukanti-and-store',
  quickLinks: [],
  contact: {
    whatsapp: true,
    phone: true,
  },
  socialLinks: [],
};

export const DEFAULT_STOREFRONT_MOBILE_NAV_CHROME_CONFIG: StorefrontMobileNavChromeConfig = {
  items: [
    { id: 'home', target: { type: 'store-home' } },
    { id: 'sales', target: { type: 'store-sales' } },
    { id: 'orders', target: { type: 'customer-orders' } },
    { id: 'account', target: { type: 'customer-account' } },
  ],
};

/** F-142. Platform default: no text, no link. The section itself defaults to `status: 'hidden'`. */
export const DEFAULT_STOREFRONT_ANNOUNCEMENT_BAR_CHROME_CONFIG: StorefrontAnnouncementBarChromeConfig =
  {
    text: { ar: '', he: '', en: '' },
    link: null,
  };

export const DEFAULT_STOREFRONT_GLOBAL_CHROME_SETTINGS: StorefrontResolvedGlobalChromeSettings = {
  announcementBar: { enabled: false, ...DEFAULT_STOREFRONT_ANNOUNCEMENT_BAR_CHROME_CONFIG },
  header: DEFAULT_STOREFRONT_HEADER_CHROME_CONFIG,
  footer: DEFAULT_STOREFRONT_FOOTER_CHROME_CONFIG,
  mobileNav: DEFAULT_STOREFRONT_MOBILE_NAV_CHROME_CONFIG,
};

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const ID_RE = /^[a-z0-9][a-z0-9_-]{1,39}$/;
const INVALID_PERCENT_RE = /%(?![0-9A-Fa-f]{2})/;
const WHITESPACE_RE = /\s/;
const INSTAGRAM_RESERVED_PATHS = new Set([
  'about',
  'accounts',
  'api',
  'developer',
  'direct',
  'explore',
  'legal',
  'oauth',
  'p',
  'reel',
  'reels',
  'stories',
]);
const FACEBOOK_RESERVED_PATHS = new Set([
  'about',
  'business',
  'checkpoint',
  'dialog',
  'events',
  'gaming',
  'groups',
  'help',
  'l.php',
  'login',
  'marketplace',
  'messages',
  'pages',
  'plugins',
  'privacy',
  'profile.php',
  'recover',
  'share',
  'sharer',
  'watch',
]);

function hasControlOrBidiCharacters(value: string): boolean {
  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0;
    if (
      codePoint <= 0x1f ||
      codePoint === 0x7f ||
      (codePoint >= 0x202a && codePoint <= 0x202e) ||
      (codePoint >= 0x2066 && codePoint <= 0x2069)
    ) {
      return true;
    }
  }
  return false;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

function hasExactNoneDataSource(section: StorefrontAnySectionConfig): boolean {
  const source = section.dataSource;
  return isRecord(source) && Object.keys(source).length === 1 && source.kind === 'none';
}

function isOneOf<T extends string>(value: unknown, values: readonly T[]): value is T {
  return typeof value === 'string' && values.includes(value as T);
}

function isSafeId(value: unknown): value is string {
  return typeof value === 'string' && ID_RE.test(value);
}

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

function hasUniqueIds(items: readonly { readonly id: string }[]): boolean {
  const ids = new Set<string>();
  for (const item of items) {
    if (ids.has(item.id)) return false;
    ids.add(item.id);
  }
  return true;
}

function isStoreCollectionKey(value: unknown): value is StoreCollectionKey {
  return isOneOf(value, STOREFRONT_COLLECTION_NAVIGATION_KEYS);
}

export function isStorefrontHeaderNavigationTarget(
  value: unknown,
): value is StorefrontHeaderNavigationTarget {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  switch (value.type) {
    case 'store-home':
    case 'store-categories':
    case 'store-sales':
    case 'cart':
      return hasOnlyKeys(value, ['type']);
    case 'store-collection':
      return (
        hasOnlyKeys(value, ['type', 'collectionKey']) && isStoreCollectionKey(value.collectionKey)
      );
    case 'store-category':
      return hasOnlyKeys(value, ['type', 'categoryId']) && isUuid(value.categoryId);
    default:
      return false;
  }
}

export function isStorefrontFooterQuickLinkTarget(
  value: unknown,
): value is StorefrontFooterQuickLinkTarget {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  if (isStorefrontHeaderNavigationTarget(value)) return true;
  switch (value.type) {
    case 'customer-account':
    case 'customer-orders':
      return hasOnlyKeys(value, ['type']);
    case 'contact':
      return (
        hasOnlyKeys(value, ['type', 'method']) &&
        isOneOf(value.method, STOREFRONT_NAVIGATION_CONTACT_METHODS)
      );
    default:
      return false;
  }
}

export function isStorefrontMobileNavigationTarget(
  value: unknown,
): value is StorefrontMobileNavigationTarget {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  switch (value.type) {
    case 'store-home':
    case 'store-categories':
    case 'store-sales':
    case 'cart':
    case 'customer-account':
    case 'customer-orders':
      return hasOnlyKeys(value, ['type']);
    default:
      return false;
  }
}

function isNavigationItem<TTarget extends StorefrontNavigationTarget>(
  value: unknown,
  isTarget: (target: unknown) => target is TTarget,
): value is StorefrontNavigationItem<TTarget> {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['id', 'target']) &&
    isSafeId(value.id) &&
    isTarget(value.target)
  );
}

function isNavigationItemArray<TTarget extends StorefrontNavigationTarget>(
  value: unknown,
  max: number,
  isTarget: (target: unknown) => target is TTarget,
): value is readonly StorefrontNavigationItem<TTarget>[] {
  if (!Array.isArray(value) || value.length > max) return false;
  if (!value.every((item) => isNavigationItem(item, isTarget))) return false;
  return hasUniqueIds(value);
}

export function sanitizeStorefrontSocialProfileUrl(
  url: unknown,
  provider: StorefrontSocialProvider,
): StorefrontSafeSocialUrl | null {
  if (typeof url !== 'string') return null;
  if (
    url.trim() !== url ||
    url.length === 0 ||
    url.length > STOREFRONT_SOCIAL_URL_MAX_LENGTH ||
    WHITESPACE_RE.test(url) ||
    hasControlOrBidiCharacters(url) ||
    INVALID_PERCENT_RE.test(url) ||
    url.includes('\\')
  ) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (
    parsed.protocol !== 'https:' ||
    parsed.username !== '' ||
    parsed.password !== '' ||
    parsed.port !== '' ||
    parsed.search !== '' ||
    parsed.hash !== ''
  ) {
    return null;
  }

  const hostname = parsed.hostname.toLowerCase();
  const pathname = parsed.pathname;
  const pathWithoutTrailingSlash = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const segment = pathWithoutTrailingSlash.startsWith('/')
    ? pathWithoutTrailingSlash.slice(1)
    : pathWithoutTrailingSlash;
  if (!segment || segment.includes('/')) return null;

  const reservedKey = segment.toLowerCase();
  if (provider === 'instagram') {
    if (hostname !== 'instagram.com' && hostname !== 'www.instagram.com') return null;
    if (INSTAGRAM_RESERVED_PATHS.has(reservedKey)) return null;
    if (!/^[A-Za-z0-9._]{1,30}$/.test(segment)) return null;
    return parsed.href as StorefrontSafeSocialUrl;
  }

  if (hostname !== 'facebook.com' && hostname !== 'www.facebook.com') return null;
  if (FACEBOOK_RESERVED_PATHS.has(reservedKey)) return null;
  if (!/^[A-Za-z0-9.]{5,50}$/.test(segment)) return null;
  return parsed.href as StorefrontSafeSocialUrl;
}

export function isStorefrontSafeSocialUrl(
  url: unknown,
  provider: StorefrontSocialProvider,
): url is StorefrontSafeSocialUrl {
  return sanitizeStorefrontSocialProfileUrl(url, provider) !== null;
}

export function isStorefrontSocialLink(value: unknown): value is StorefrontSocialLink {
  if (!isRecord(value) || !hasOnlyKeys(value, ['id', 'provider', 'url']) || !isSafeId(value.id)) {
    return false;
  }
  if (!isOneOf(value.provider, STOREFRONT_SOCIAL_PROVIDERS)) return false;
  return isStorefrontSafeSocialUrl(value.url, value.provider);
}

function sanitizeSocialLink(value: unknown): StorefrontSocialLink | null {
  if (!isRecord(value) || !hasOnlyKeys(value, ['id', 'provider', 'url']) || !isSafeId(value.id)) {
    return null;
  }
  if (!isOneOf(value.provider, STOREFRONT_SOCIAL_PROVIDERS)) return null;
  const url = sanitizeStorefrontSocialProfileUrl(value.url, value.provider);
  return url ? { id: value.id, provider: value.provider, url } : null;
}

export function isStorefrontHeaderChromeConfig(
  value: unknown,
): value is StorefrontHeaderChromeConfig {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['brandDisplay', 'primaryLinks']) &&
    isOneOf(value.brandDisplay, STOREFRONT_HEADER_BRAND_DISPLAY_OPTIONS) &&
    isNavigationItemArray(
      value.primaryLinks,
      STOREFRONT_HEADER_PRIMARY_LINK_LIMIT,
      isStorefrontHeaderNavigationTarget,
    )
  );
}

export function isStorefrontFooterChromeConfig(
  value: unknown,
): value is StorefrontFooterChromeConfig {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['brandDisplay', 'quickLinks', 'contact', 'socialLinks']) ||
    !isOneOf(value.brandDisplay, STOREFRONT_FOOTER_BRAND_DISPLAY_OPTIONS) ||
    !isNavigationItemArray(
      value.quickLinks,
      STOREFRONT_FOOTER_QUICK_LINK_LIMIT,
      isStorefrontFooterQuickLinkTarget,
    ) ||
    !isRecord(value.contact) ||
    !hasOnlyKeys(value.contact, ['whatsapp', 'phone']) ||
    typeof value.contact.whatsapp !== 'boolean' ||
    typeof value.contact.phone !== 'boolean' ||
    !Array.isArray(value.socialLinks) ||
    value.socialLinks.length > STOREFRONT_FOOTER_SOCIAL_LINK_LIMIT ||
    !value.socialLinks.every(isStorefrontSocialLink)
  ) {
    return false;
  }
  return hasUniqueIds(value.socialLinks);
}

export function isStorefrontMobileNavChromeConfig(
  value: unknown,
): value is StorefrontMobileNavChromeConfig {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['items']) &&
    Array.isArray(value.items) &&
    value.items.length >= STOREFRONT_MOBILE_NAV_ITEM_MIN &&
    value.items.length <= STOREFRONT_MOBILE_NAV_ITEM_MAX &&
    value.items.every((item: unknown): item is StorefrontMobileNavItem =>
      isNavigationItem(item, isStorefrontMobileNavigationTarget),
    ) &&
    hasUniqueIds(value.items)
  );
}

export function getStorefrontHeaderChromeConfigIssue(
  value: unknown,
): StorefrontValidationErrorCode | null {
  if (isStorefrontHeaderChromeConfig(value)) return null;
  return 'invalid_navigation_target';
}

export function getStorefrontFooterChromeConfigIssue(
  value: unknown,
): StorefrontValidationErrorCode | null {
  if (!isRecord(value)) return 'invalid_navigation_target';
  if (Array.isArray(value.socialLinks)) {
    for (const socialLink of value.socialLinks) {
      if (isRecord(socialLink) && !sanitizeSocialLink(socialLink)) return 'unsafe_navigation_url';
    }
  }
  return isStorefrontFooterChromeConfig(value) ? null : 'invalid_navigation_target';
}

export function getStorefrontMobileNavChromeConfigIssue(
  value: unknown,
): StorefrontValidationErrorCode | null {
  if (isStorefrontMobileNavChromeConfig(value)) return null;
  return 'invalid_navigation_target';
}

/** F-142. A header navigation target minus `store-category` (no merchant-row references). */
export function isStorefrontAnnouncementBarLinkTarget(
  value: unknown,
): value is StorefrontAnnouncementBarLinkTarget {
  return isStorefrontHeaderNavigationTarget(value) && value.type !== 'store-category';
}

export function isStorefrontAnnouncementBarChromeConfig(
  value: unknown,
): value is StorefrontAnnouncementBarChromeConfig {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['text', 'link']) &&
    'text' in value &&
    'link' in value &&
    sanitizeStorefrontSeoLocalizedText(value.text, STOREFRONT_ANNOUNCEMENT_BAR_TEXT_MAX_LENGTH) !==
      null &&
    (value.link === null || isStorefrontAnnouncementBarLinkTarget(value.link))
  );
}

export function getStorefrontAnnouncementBarChromeConfigIssue(
  value: unknown,
): StorefrontValidationErrorCode | null {
  if (isStorefrontAnnouncementBarChromeConfig(value)) return null;
  if (
    isRecord(value) &&
    value.link !== null &&
    value.link !== undefined &&
    !isStorefrontAnnouncementBarLinkTarget(value.link)
  ) {
    return 'invalid_navigation_target';
  }
  return 'invalid_config';
}

/** Returns normalized text (trimmed, whitespace collapsed) and a cloned link, or null when invalid. */
export function sanitizeStorefrontAnnouncementBarChromeConfig(
  value: unknown,
): StorefrontAnnouncementBarChromeConfig | null {
  if (!isStorefrontAnnouncementBarChromeConfig(value)) return null;
  const text = sanitizeStorefrontSeoLocalizedText(
    value.text,
    STOREFRONT_ANNOUNCEMENT_BAR_TEXT_MAX_LENGTH,
  );
  if (!text) return null;
  return { text, link: value.link === null ? null : structuredClone(value.link) };
}

export function getDefaultStorefrontAnnouncementBarChromeConfig(): StorefrontAnnouncementBarChromeConfig {
  return structuredClone(DEFAULT_STOREFRONT_ANNOUNCEMENT_BAR_CHROME_CONFIG);
}

export function resolveStorefrontAnnouncementBarChromeConfig(
  value: unknown,
): StorefrontAnnouncementBarChromeConfig {
  return (
    sanitizeStorefrontAnnouncementBarChromeConfig(value) ??
    getDefaultStorefrontAnnouncementBarChromeConfig()
  );
}

/** Exact locale, then ar → he → en; `''` when every locale is empty (the bar then renders nothing). */
export function resolveStorefrontAnnouncementBarText(args: {
  readonly value: StorefrontHeroLocalizedText | null | undefined;
  readonly locale: string | null | undefined;
}): string {
  return resolveStorefrontSeoText({ value: args.value, locale: args.locale, fallbackText: '' });
}

export function isStorefrontAnnouncementBarSettings(
  value: unknown,
): value is StorefrontAnnouncementBarSettings {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['enabled', 'text', 'link']) &&
    typeof value.enabled === 'boolean' &&
    isStorefrontAnnouncementBarChromeConfig({ text: value.text, link: value.link })
  );
}

export function sanitizeStorefrontHeaderChromeConfig(
  value: unknown,
): StorefrontHeaderChromeConfig | null {
  return isStorefrontHeaderChromeConfig(value) ? structuredClone(value) : null;
}

export function sanitizeStorefrontFooterChromeConfig(
  value: unknown,
): StorefrontFooterChromeConfig | null {
  if (!isRecord(value) || !isStorefrontFooterChromeConfig(value)) return null;
  return {
    brandDisplay: value.brandDisplay,
    quickLinks: structuredClone(value.quickLinks),
    contact: structuredClone(value.contact),
    socialLinks: value.socialLinks.map((link) => {
      const sanitized = sanitizeSocialLink(link);
      if (!sanitized) throw new Error('[storefrontNavigation] unexpected unsafe social link');
      return sanitized;
    }),
  };
}

export function sanitizeStorefrontMobileNavChromeConfig(
  value: unknown,
): StorefrontMobileNavChromeConfig | null {
  return isStorefrontMobileNavChromeConfig(value) ? structuredClone(value) : null;
}

export function getDefaultStorefrontHeaderChromeConfig(): StorefrontHeaderChromeConfig {
  return structuredClone(DEFAULT_STOREFRONT_HEADER_CHROME_CONFIG);
}

export function getDefaultStorefrontFooterChromeConfig(): StorefrontFooterChromeConfig {
  return structuredClone(DEFAULT_STOREFRONT_FOOTER_CHROME_CONFIG);
}

export function getDefaultStorefrontMobileNavChromeConfig(): StorefrontMobileNavChromeConfig {
  return structuredClone(DEFAULT_STOREFRONT_MOBILE_NAV_CHROME_CONFIG);
}

export function classifyStorefrontGlobalChromeEditableSurface(
  section: StorefrontAnySectionConfig,
): StorefrontGlobalChromeEditableSurface | null {
  if (section.status !== 'enabled' || !hasExactNoneDataSource(section)) return null;

  const definition = GLOBAL_CHROME_EDITABLE_SURFACES.find(
    (candidate) =>
      section.id === candidate.id &&
      section.type === candidate.type &&
      section.placement === candidate.placement,
  );
  return definition?.surface ?? null;
}

export function resolveStorefrontHeaderChromeConfig(value: unknown): StorefrontHeaderChromeConfig {
  return sanitizeStorefrontHeaderChromeConfig(value) ?? getDefaultStorefrontHeaderChromeConfig();
}

export function resolveStorefrontFooterChromeConfig(value: unknown): StorefrontFooterChromeConfig {
  return sanitizeStorefrontFooterChromeConfig(value) ?? getDefaultStorefrontFooterChromeConfig();
}

export function resolveStorefrontMobileNavChromeConfig(
  value: unknown,
): StorefrontMobileNavChromeConfig {
  return (
    sanitizeStorefrontMobileNavChromeConfig(value) ?? getDefaultStorefrontMobileNavChromeConfig()
  );
}

type StorefrontChromeContentKey =
  (typeof GLOBAL_CHROME_SECTION_BY_TYPE)[StorefrontGlobalChromeSectionType]['contentKey'];

function sectionChromeKey(
  sectionType: StorefrontSectionConfigV2['type'],
): StorefrontChromeContentKey | null {
  if (!isStorefrontGlobalChromeSectionType(sectionType)) return null;
  return GLOBAL_CHROME_SECTION_BY_TYPE[sectionType].contentKey;
}

function defaultChromeContentFor(
  key: StorefrontChromeContentKey,
): Partial<StorefrontSectionContentConfig> {
  switch (key) {
    case 'announcementBar':
      return { announcementBar: getDefaultStorefrontAnnouncementBarChromeConfig() };
    case 'header':
      return { header: getDefaultStorefrontHeaderChromeConfig() };
    case 'footer':
      return { footer: getDefaultStorefrontFooterChromeConfig() };
    case 'mobileNav':
      return { mobileNav: getDefaultStorefrontMobileNavChromeConfig() };
  }
}

function sectionWithDefaultChromeConfig(
  section: StorefrontSectionConfigV2,
): StorefrontSectionConfigV2 {
  const key = sectionChromeKey(section.type);
  if (!key) return section;
  const defaultContent = defaultChromeContentFor(key);
  return {
    ...section,
    config: {
      ...(section.config ?? {}),
      content: {
        ...defaultContent,
        ...(section.config?.content ?? {}),
      },
    },
  } as StorefrontSectionConfigV2;
}

export function withDefaultStorefrontGlobalChromeConfigs(
  config: StorefrontPageConfigV2,
): StorefrontPageConfigV2 {
  return {
    ...structuredClone(config),
    sections: config.sections.map(sectionWithDefaultChromeConfig),
  };
}

function globalSection(
  config: StorefrontPageConfig | null | undefined,
  type: StorefrontGlobalChromeSectionType,
): StorefrontSectionConfigV2 | undefined {
  if (!config || config.engineVersion !== 2) return undefined;
  return config.sections.find((section) => section.type === type);
}

function globalSectionContent(
  config: StorefrontPageConfig | null | undefined,
  type: StorefrontGlobalChromeSectionType,
): unknown {
  return globalSection(config, type)?.config?.content;
}

export function resolveStorefrontGlobalChromeSettings(
  config: StorefrontPageConfig | null | undefined,
): StorefrontResolvedGlobalChromeSettings {
  const barSection = globalSection(config, 'global.announcementBar');
  const barContent = barSection?.config?.content;
  const headerContent = globalSectionContent(config, 'global.header');
  const footerContent = globalSectionContent(config, 'global.footer');
  const mobileNavContent = globalSectionContent(config, 'global.mobileNav');
  return {
    announcementBar: {
      enabled: barSection?.status === 'enabled',
      ...resolveStorefrontAnnouncementBarChromeConfig(
        isRecord(barContent) ? barContent.announcementBar : undefined,
      ),
    },
    header: resolveStorefrontHeaderChromeConfig(
      isRecord(headerContent) ? headerContent.header : undefined,
    ),
    footer: resolveStorefrontFooterChromeConfig(
      isRecord(footerContent) ? footerContent.footer : undefined,
    ),
    mobileNav: resolveStorefrontMobileNavChromeConfig(
      isRecord(mobileNavContent) ? mobileNavContent.mobileNav : undefined,
    ),
  };
}

export function isStorefrontGlobalChromeSettings(
  settings: unknown,
): settings is StorefrontGlobalChromeSettings {
  return (
    isRecord(settings) &&
    // The key allow-list rejects UNKNOWN keys; it does not mandate every known one. The bar is
    // optional (pre-F-142 callers omit it); when present it must be valid.
    hasOnlyKeys(settings, ['announcementBar', 'header', 'footer', 'mobileNav']) &&
    (settings.announcementBar === undefined ||
      isStorefrontAnnouncementBarSettings(settings.announcementBar)) &&
    isStorefrontHeaderChromeConfig(settings.header) &&
    isStorefrontFooterChromeConfig(settings.footer) &&
    isStorefrontMobileNavChromeConfig(settings.mobileNav)
  );
}

export function withStorefrontGlobalChromeSettings(
  config: StorefrontPageConfig,
  settings: StorefrontGlobalChromeSettings,
): StorefrontPageConfig {
  if (!isStorefrontGlobalChromeSettings(settings) || config.engineVersion !== 2) {
    throw new Error('[storefrontNavigation] Invalid global chrome settings');
  }
  return {
    ...structuredClone(config),
    sections: config.sections.map((section) => {
      const key = sectionChromeKey(section.type);
      if (!key) return section;
      if (key === 'announcementBar') {
        // Backward compatibility (F-142): a caller that omits `announcementBar` predates the bar
        // and must leave it exactly as it is — never reset it to the platform default.
        if (settings.announcementBar === undefined) return section;
        // The bar is the ONLY chrome section whose status this path writes: its contract is
        // toggleable (canHide/canShow). Header/footer/mobileNav have canHide:false and their
        // status is never touched here (a hidden header could never be re-shown from the UI).
        const { enabled, text, link } = settings.announcementBar;
        return {
          ...section,
          status: enabled ? 'enabled' : 'hidden',
          config: {
            ...(section.config ?? {}),
            content: {
              ...(section.config?.content ?? {}),
              announcementBar: structuredClone({ text, link }),
            },
          },
        } as StorefrontSectionConfigV2;
      }
      const value =
        key === 'header'
          ? settings.header
          : key === 'footer'
            ? settings.footer
            : settings.mobileNav;
      return {
        ...section,
        config: {
          ...(section.config ?? {}),
          content: {
            ...(section.config?.content ?? {}),
            [key]: structuredClone(value),
          },
        },
      } as StorefrontSectionConfigV2;
    }),
  };
}

export function resetStorefrontGlobalChromeSettings(
  config: StorefrontPageConfig,
): StorefrontPageConfig {
  return withStorefrontGlobalChromeSettings(config, DEFAULT_STOREFRONT_GLOBAL_CHROME_SETTINGS);
}

export function validateOptionalStorefrontGlobalChromeSettings(settings: unknown): boolean {
  return settings === undefined || isStorefrontGlobalChromeSettings(settings);
}

function encodeSlug(slug: string): string {
  return encodeURIComponent(slug);
}

function storePath(slug: string): string {
  return `/s/${encodeSlug(slug)}`;
}

function storePreviewBasePath(slug: string): StorefrontNavigationRouteBase {
  return `${storePath(slug)}/preview` as StorefrontNavigationRouteBase;
}

function normalizeStorefrontNavigationRouteBase(
  store: PublicStore,
  routeBase: StorefrontNavigationRouteBase | undefined,
): StorefrontNavigationRouteBase {
  const publicRouteBase = storePath(store.slug) as StorefrontNavigationRouteBase;
  const previewRouteBase = storePreviewBasePath(store.slug);
  return routeBase === previewRouteBase ? previewRouteBase : publicRouteBase;
}

function isPreviewNavigationRouteBase(
  store: PublicStore,
  routeBase: StorefrontNavigationRouteBase,
): boolean {
  return routeBase === storePreviewBasePath(store.slug);
}

function previewUnavailablePath(routeBase: StorefrontNavigationRouteBase): string {
  return `${routeBase}/unavailable`;
}

function withStoreContext(path: string, slug: string): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}store=${encodeURIComponent(slug)}`;
}

function buildContactHref(
  target: Extract<StorefrontFooterQuickLinkTarget, { readonly type: 'contact' }>,
  store: PublicStore,
): string | null {
  if (target.method === 'whatsapp') {
    return store.whatsapp_number
      ? `https://wa.me/${store.whatsapp_number.replace(/^\+/, '')}`
      : null;
  }
  return store.phone_number ? `tel:${store.phone_number}` : null;
}

export function buildStorefrontNavigationHref(
  target: StorefrontNavigationTarget,
  store: PublicStore,
  options: StorefrontNavigationHrefOptions = {},
): string | null {
  const routeBase = normalizeStorefrontNavigationRouteBase(store, options.routeBase);
  const isPreviewRoute = isPreviewNavigationRouteBase(store, routeBase);

  switch (target.type) {
    case 'store-home':
      return routeBase;
    case 'store-categories':
      return `${routeBase}/categories`;
    case 'store-sales':
      return `${routeBase}/sales`;
    case 'store-collection':
      if (isPreviewRoute) return previewUnavailablePath(routeBase);
      return `${storePath(store.slug)}/collections/${toStoreCollectionRouteKey(target.collectionKey)}`;
    case 'store-category':
      if (isPreviewRoute) return previewUnavailablePath(routeBase);
      return `${storePath(store.slug)}/c/${encodeURIComponent(target.categoryId)}`;
    case 'cart':
      if (isPreviewRoute) return previewUnavailablePath(routeBase);
      return `${storePath(store.slug)}/cart`;
    case 'customer-account':
      if (isPreviewRoute) return previewUnavailablePath(routeBase);
      return withStoreContext('/account', store.slug);
    case 'customer-orders':
      if (isPreviewRoute) return previewUnavailablePath(routeBase);
      return withStoreContext('/account/orders', store.slug);
    case 'contact':
      return buildContactHref(target, store);
  }
}

export function getStorefrontNavigationLabelKey(
  target: StorefrontNavigationTarget | StorefrontSocialLink,
): TranslationKey {
  if ('provider' in target) {
    return target.provider === 'instagram'
      ? 'storefront.storeChrome.labels.instagram'
      : 'storefront.storeChrome.labels.facebook';
  }
  switch (target.type) {
    case 'store-home':
      return 'storefront.storeChrome.labels.storeHome';
    case 'store-categories':
      return 'storefront.storeChrome.labels.storeCategories';
    case 'store-sales':
      return 'storefront.storeChrome.labels.storeSales';
    case 'store-collection':
      if (target.collectionKey === 'best_sellers') {
        return 'storefront.storeChrome.labels.bestSellers';
      }
      if (target.collectionKey === 'trending_now') {
        return 'storefront.storeChrome.labels.trendingNow';
      }
      return 'storefront.storeChrome.labels.newArrivals';
    case 'store-category':
      return 'storefront.storeChrome.labels.category';
    case 'cart':
      return 'storefront.storeChrome.labels.cart';
    case 'customer-account':
      return 'storefront.storeChrome.labels.account';
    case 'customer-orders':
      return 'storefront.storeChrome.labels.orders';
    case 'contact':
      return target.method === 'whatsapp'
        ? 'storefront.storeChrome.labels.whatsapp'
        : 'storefront.storeChrome.labels.phone';
  }
}

export function buildStorefrontNavigationViews<TTarget extends StorefrontNavigationTarget>(args: {
  readonly items: readonly StorefrontNavigationItem<TTarget>[];
  readonly store: PublicStore;
  readonly routeBase?: StorefrontNavigationRouteBase;
}): readonly StorefrontNavigationView[] {
  return args.items.flatMap((item) => {
    const href = buildStorefrontNavigationHref(item.target, args.store, {
      routeBase: args.routeBase,
    });
    if (!href) return [];
    return [
      {
        id: item.id,
        target: item.target,
        href,
        labelKey: getStorefrontNavigationLabelKey(item.target),
        isExternal: href.startsWith('https://'),
      },
    ];
  });
}

export function buildStorefrontSocialLinkViews(args: {
  readonly links: readonly StorefrontSocialLink[];
}): readonly StorefrontNavigationView[] {
  return args.links.map((link) => ({
    id: link.id,
    target: { type: 'social', provider: link.provider },
    href: link.url,
    labelKey: getStorefrontNavigationLabelKey(link),
    isExternal: true,
  }));
}

function targetRequiresRemoteReadiness(target: StorefrontNavigationTarget): boolean {
  return target.type === 'store-category' || target.type === 'contact';
}

export function storefrontChromeConfigRequiresRemoteReadiness(
  config: StorefrontPageConfig | null | undefined,
): boolean {
  const chrome = resolveStorefrontGlobalChromeSettings(config);
  return (
    chrome.header.primaryLinks.some((item) => targetRequiresRemoteReadiness(item.target)) ||
    chrome.footer.quickLinks.some((item) => targetRequiresRemoteReadiness(item.target))
  );
}
